import { useState } from "react";
import type { CreateInventoryItem, InventoryKind } from "@dentacore/shared-types";

import { parseMoney } from "../patients/money";
import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import { COUNT, KINDS, USES } from "./stock";
import styles from "./inventory.module.css";
import { RequiredMark } from "../ui/required-mark";
import { useBranchCurrency } from "../api/use-branch-currency";

/**
 * What every pack is called now that nobody is asked (FX21). The column stays —
 * existing items keep what was typed, and a supplier's price line still names
 * its own pack — but *Add an item* no longer asks for a word nobody used.
 */
const PACK = "pack";

/**
 * *Add an item*, as a dialog (IV9) — **five questions since FX20–FX22**: name,
 * kind, how big a pack is, its price, and when to warn.
 *
 * ── What went, and why ─────────────────────────────────────────────────────
 *
 * *What a pack is called* (FX21), *Opening count* and *A full shelf* (FX20)
 * were asked of every item and read by almost nothing — stock now starts at
 * zero and is added from the item's panel, the same Add every later delivery
 * uses, and an order with no full shelf suggests one pack as it always did.
 *
 * **One pack-size question, never two** (FX20, FX22). A bulk item asked both
 * "uses per pack" and "about how many uses in one pack", which said the same
 * thing twice. Now a **counted** item asks how many uses a pack holds, and a
 * **bulk** item is counted in whole packs — adding one adds a box, the alert is
 * in boxes — so it asks only how many are in one, for costing (MC-D4).
 *
 * **The counts go through `NumberField`** (QA20-13), and **the price per pack is
 * required** (IP-D1), parsed by `parseMoney` against the currency's exponent.
 */
export function AddItemDialog({
  busy,
  onCreate,
  onClose,
  location,
}: {
  /** Where the item goes, when several locations are shown. */
  location?: React.ReactNode;
  busy: boolean;
  /** Resolves true once the item exists, so the dialog closes only then. */
  onCreate: (body: CreateInventoryItem) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<InventoryKind>("COUNTABLE");
  // A pack is one use unless somebody says otherwise — a property of packs,
  // not a clinic's data.
  const [uses, setUses] = useState("1");
  // Required (IC7): every item carries its own low threshold. No default —
  // the number is the clinic's, and a guessed one is the thing IC7 removed.
  const [lowAt, setLowAt] = useState("");
  const [price, setPrice] = useState("");
  const [estimate, setEstimate] = useState("");
  const isBulk = kind === "BULK";
  const isCounted = kind === "COUNTABLE";
  const currency = useBranchCurrency();
  const priceCents = parseMoney(price, currency);
  /** What the threshold is counted in — packs for bulk (FX22). */
  // The threshold is asked in packs wherever a pack holds several uses
  // (QA4-D2) — the box, not the glove — and stored as the item counts.
  const usesInPack = isCounted && Number.isInteger(Number(uses)) ? Math.max(1, Number(uses)) : 1;
  const unit = isBulk || usesInPack > 1 ? "packs" : "uses";

  const blocked =
    priceCents === null ||
    name.trim() === "" ||
    (isCounted && (uses.trim() === "" || describeNumberError(uses, USES) !== null)) ||
    lowAt.trim() === "" ||
    describeNumberError(lowAt, COUNT) !== null ||
    (isBulk && (estimate.trim() === "" || describeNumberError(estimate, USES) !== null));

  return (
    <Modal label="Add an item" onClose={onClose} testId="inventory-add-dialog">
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (blocked) return;
          void onCreate({
            name: name.trim(),
            kind,
            stockUnit: PACK,
            // Only a counted item has uses inside a pack; bulk counts whole
            // packs and a reusable instrument is one thing (FX22).
            usesPerStockUnit: isCounted ? Number(uses) : 1,
            // `blocked` already refused a missing or unparseable price.
            priceCents: priceCents ?? 0,
            // Starts empty; stock arrives through the panel's Add (FX20).
            quantityOnHand: 0,
            lowAt: Number(lowAt) * (isCounted ? usesInPack : 1),
            // Only a bulk item carries one; the server refuses it on any other.
            ...(isBulk ? { estimatedUses: Number(estimate) } : {}),
          }).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={styles.dialogTitle}>Add an item</h2>
        {location}

        <div className={styles.dialogGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Name
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Whatever you call it"
              autoFocus
              data-testid="inventory-name"
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Kind
              <RequiredMark />
            </span>
            <select
              className={styles.input}
              value={kind}
              onChange={(event) => setKind(event.target.value as InventoryKind)}
              data-testid="inventory-kind"
            >
              {KINDS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {isCounted && (
            <NumberField
              label="Uses per pack"
              required
              className={styles.field}
              labelClassName={styles.fieldLabel}
              inputClassName={styles.input}
              value={uses}
              onChange={setUses}
              rules={USES}
              hint="A box of 20 is 20. Each one taken off a procedure comes out of it."
              testId="inventory-uses-per-pack"
            />
          )}

          {isBulk && (
            <NumberField
              label="How many in one pack?"
              required
              className={styles.field}
              labelClassName={styles.fieldLabel}
              inputClassName={styles.input}
              value={estimate}
              onChange={setEstimate}
              rules={USES}
              hint="For costing procedures. Stock is counted in whole packs."
              testId="inventory-estimated-uses"
            />
          )}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Price per pack
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder={`What one pack costs, in ${currency}`}
              aria-invalid={price.trim() !== "" && priceCents === null}
              aria-describedby="inventory-price-error"
              data-testid="inventory-price"
            />
            {price.trim() !== "" && priceCents === null && (
              <span className={styles.fieldError} id="inventory-price-error" role="alert">
                Type an amount above zero.
              </span>
            )}
          </label>

          <NumberField
            label={`Alert when this many ${unit} are left`}
            required
            className={styles.field}
            labelClassName={styles.fieldLabel}
            inputClassName={styles.input}
            value={lowAt}
            onChange={setLowAt}
            rules={{ ...COUNT, unit }}
            hint="At or below it, after what is booked, the item is marked low and Home is told."
            testId="inventory-low-at"
          />
        </div>

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || blocked}
            data-testid="inventory-add-save"
          >
            Add it
          </button>
        </div>
      </form>
    </Modal>
  );
}
